<script lang="ts">
  import type { Attachment } from '../lib/types';

  interface Props {
    attachment: Attachment;
    onremove?: (att: Attachment) => void;
  }

  let { attachment, onremove }: Props = $props();

  const isImage = $derived(attachment.content_type.startsWith('image/'));
  const isPdf = $derived(attachment.content_type === 'application/pdf');
  const isVideo = $derived(attachment.content_type.startsWith('video/'));
  const sizeKb = $derived(Math.max(1, Math.round(attachment.size_bytes / 1024)));
  const sizeLabel = $derived(
    sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`,
  );
</script>

<div class="hd-att" class:hd-att--chip={!!onremove} title={attachment.original_name ?? attachment.storage_key}>
  {#if isImage && attachment.preview_url}
    <img src={attachment.preview_url} alt={attachment.original_name ?? 'attachment'} />
  {:else}
    <div class="hd-att__icon" aria-hidden="true">
      {#if isPdf}
        PDF
      {:else if isVideo}
        VID
      {:else if isImage}
        IMG
      {:else}
        FILE
      {/if}
    </div>
  {/if}
  <div class="hd-att__meta">
    <span class="hd-att__name">{attachment.original_name ?? 'attachment'}</span>
    <span class="hd-att__size">{sizeLabel}</span>
  </div>
  {#if onremove}
    <button
      type="button"
      class="hd-att__remove"
      aria-label="Quitar archivo"
      title="Quitar archivo"
      onclick={() => onremove?.(attachment)}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M6 6l12 12M18 6L6 18"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        />
      </svg>
    </button>
  {/if}
</div>

<style>
  .hd-att {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: var(--hd-surface-muted, #f8fafc);
    border: 1px solid var(--hd-border, #e2e8f0);
    border-radius: 8px;
    max-width: 220px;
    font-size: 12px;
    color: var(--hd-fg, #0f172a);
  }
  .hd-att--chip {
    height: 32px;
    padding: 4px 8px;
    border-radius: 8px;
  }
  .hd-att img {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    object-fit: cover;
    flex: 0 0 auto;
  }
  .hd-att__icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    flex: 0 0 auto;
  }
  .hd-att--chip .hd-att__icon,
  .hd-att--chip img {
    width: 24px;
    height: 24px;
    font-size: 9px;
  }
  .hd-att__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
  }
  .hd-att__name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
    font-weight: 500;
  }
  .hd-att__size {
    color: var(--hd-fg-muted, #64748b);
    font-size: 10px;
  }
  .hd-att__remove {
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--hd-fg-muted, #64748b);
    padding: 2px;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    display: grid;
    place-items: center;
    transition:
      background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-att__remove:hover {
    color: #ef4444;
    background: rgb(239 68 68 / 0.1);
  }
  .hd-att__remove svg {
    width: 12px;
    height: 12px;
  }
</style>
